import React, { useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { CheckCircle2, XCircle } from 'lucide-react';
import correctSound from '../assets/sounds/correct.mp3';
import wrongSound from '../assets/sounds/wrong.mp3';

const QuestionDialog = ({ questions, isOpen, onClose }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  
  // Create audio elements
  const correctAudio = new Audio(correctSound);
  const wrongAudio = new Audio(wrongSound);

  const currentQuestion = questions[currentQuestionIndex];

  const handleOptionClick = (option) => {
    setSelectedAnswer(option);
    setIsCorrect(option === currentQuestion.correct);
    setShowFeedback(true);

    // Play sound based on correctness
    if (option === currentQuestion.correct) {
      correctAudio.play();
    } else {
      wrongAudio.play();
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex === questions.length - 1) {
      onClose();
      return;
    }
    
    setCurrentQuestionIndex(prev => prev + 1);
    setSelectedAnswer(null);
    setShowFeedback(false);
  };

  if (!isOpen || !currentQuestion) return null;

return (
    <div className={`fixed inset-0 z-50 ${isOpen ? 'flex' : 'hidden'} items-center justify-center`}>
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-50 w-full max-w-2xl mx-4">
        <div className="bg-gradient-to-b from-slate-900 to-slate-800 border-2 border-blue-400/50 shadow-lg shadow-blue-500/20 backdrop-blur-sm rounded-lg overflow-hidden">
          {/* Progress Bar */}
          <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-300"
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            />
          </div>

          <div className="p-6 relative">
            {/* Decorative Elements */}
            <div className="absolute -top-1 -left-1 w-3 h-3 bg-blue-400 rounded-full animate-ping opacity-75" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-full animate-ping opacity-75" />
            
            {/* Title and Exit Button */}
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 font-bold tracking-wider">
                問題 {currentQuestionIndex + 1} / {questions.length}
              </h2>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-400/30 rounded-lg transition-all duration-300 text-lg font-medium"
              >
                終了
              </button>
            </div>
            
            {/* Question Content */}
            <div className="space-y-6">
              <div className="p-6 rounded-lg bg-slate-800/50 border border-blue-500/20 backdrop-blur-sm">
                <p className="text-2xl font-medium text-slate-100">
                  {currentQuestion.question}
                </p>
              </div>

              {/* Options */}
              <div className="grid grid-cols-1 gap-3">
                {currentQuestion.options.map((option) => (
                  <button
                    key={option}
                    onClick={() => handleOptionClick(option)}
                    className={`
                      relative group overflow-hidden w-full
                      justify-start text-left py-6 px-4
                      transition-all duration-300 ease-out text-2xl
                      ${
                        showFeedback
                          ? option === currentQuestion.correct
                            ? 'bg-green-500/20 border-green-400 text-green-300 animate-pulse ring-2 ring-green-400 ring-opacity-50'
                            : selectedAnswer === option
                              ? 'bg-red-500/20 border-red-400 text-red-300'
                              : option === currentQuestion.correct 
                                ? 'bg-green-500/20 border-green-400 text-green-300'
                                : 'bg-slate-800/50 border-blue-500/30 text-slate-100/50'
                          : 'bg-slate-800/50 border-blue-500/30 text-slate-100 hover:bg-slate-700/50'
                      }
                      ${showFeedback ? 'pointer-events-none' : ''}
                      border backdrop-blur-sm rounded-lg
                    `}
                    disabled={showFeedback}
                  >
                    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-transparent via-slate-100/5 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                    {option}
                    {showFeedback && option === currentQuestion.correct && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <div className="animate-ping absolute right-0 w-2 h-2 bg-green-400 rounded-full" />
                        <div className="absolute right-0 w-2 h-2 bg-green-400 rounded-full" />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Feedback */}
              {showFeedback && (
                <div className={`
                  flex items-center gap-3 mt-4 p-4 rounded-lg backdrop-blur-sm
                  transition-all duration-300
                  ${isCorrect 
                    ? 'bg-green-500/10 border border-green-400/30 text-green-300' 
                    : 'bg-red-500/10 border border-red-400/30 text-red-300'}
                `}>
                  {isCorrect ? (
                    <>
                      <CheckCircle2 className="w-6 h-6 animate-pulse" />
                      <span className="font-medium">正解！よくできました！</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-6 h-6 animate-pulse" />
                      <span className="font-medium">
                        不正解。正しい答え: {currentQuestion.correct}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end">
              {showFeedback && (
                <button
                  onClick={handleNext}
                  className="
                    bg-gradient-to-r from-blue-500 to-cyan-400
                    hover:from-blue-600 hover:to-cyan-500
                    text-white font-medium py-4 px-8
                    rounded-lg transition-all duration-300
                    hover:shadow-lg hover:shadow-blue-500/20
                    hover:-translate-y-1
                  "
                >
                  {currentQuestionIndex === questions.length - 1 ? 'ミッション完了' : '次の問題へ'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionDialog;